// Loads every Wingz store from the iOrders franchise API, so a store added to
// the wingz-canada franchise group shows up here with no code changes.
// Falls back to stores-snapshot.json (refreshed by `npm run build`) if the
// live API can't be reached.
import snapshot from "./stores-snapshot.json";
import storeOverrides from "./storeOverrides";

const API_URL = "https://api.iorders.online/store/franchises/?name=wingz-canada";
const ORDER_BASE = "https://orders.iorders.online/";
const CACHE_TTL_MS = 60 * 1000;
const FETCH_TIMEOUT_MS = 10 * 1000;

const DAYS = ["SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY"];

let cache = null; // { at, promise }

async function fetchLive() {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    const res = await fetch(API_URL, {
      headers: { Accept: "application/json" },
      signal: controller.signal,
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    const list = json && json.data && json.data.franchises;
    if (!Array.isArray(list) || list.length === 0) {
      throw new Error("unexpected franchise payload");
    }
    return list;
  } finally {
    clearTimeout(timer);
  }
}

// "YYYY-MM-DD" + "HH:MM[:SS]" -> minutes since epoch (store-local wall clock).
// Arithmetic rather than Date parsing so "24:00:00" works.
function toMinutes(date, time) {
  const [y, mo, d] = String(date).split("-").map(Number);
  const [h, mi] = String(time).split(":").map(Number);
  if ([y, mo, d, h, mi].some(isNaN)) return NaN;
  return Date.UTC(y, mo - 1, d) / 60000 + h * 60 + mi;
}

// Store-local "now" in minutes. The API's current_date_time was the store's
// local clock at fetchedAt, which gives the store's UTC offset; applying it
// to the visitor's clock keeps this correct whatever timezone they're in.
function storeNow(slots, fetchedAt) {
  const cur = String(slots.current_date_time || "");
  const local = Date.parse(cur.replace(" ", "T") + "Z");
  if (isNaN(local)) return NaN;
  const offset = Math.round((local - fetchedAt) / 900000) * 900000; // nearest 15 min
  return Math.floor((Date.now() + offset) / 60000);
}

// Every ordering window as { start, end, date, day } in store-local minutes.
function windows(days) {
  const out = [];
  (days || []).forEach((day) => {
    (day.slots || []).forEach((s) => {
      const start = toMinutes(day.date, s.opening_time);
      const end = toMinutes(day.date, s.closing_time);
      if (!isNaN(start) && !isNaN(end)) out.push({ start, end, date: day.date, day: day.day });
    });
  });
  return out.sort((a, b) => a.start - b.start);
}

function convertTo12Hour(minutes) {
  const m = ((minutes % 1440) + 1440) % 1440;
  const hour = Math.floor(m / 60);
  const ampm = hour >= 12 ? "PM" : "AM";
  return `${hour % 12 || 12}:${String(m % 60).padStart(2, "0")} ${ampm}`;
}

function nextOpeningText(wins, now) {
  const next = wins.find((w) => w.start > now);
  if (!next) return "Not available";
  const today = Math.floor(now / 1440);
  if (Math.floor(next.start / 1440) === today) {
    return `Opening Today at ${convertTo12Hour(next.start)}`;
  }
  const dayName = next.day || DAYS[new Date(next.date + "T00:00:00Z").getUTCDay()];
  return `Reopens On ${dayName} at ${convertTo12Hour(next.start)}`;
}

function closingSoonText(wins, now) {
  const current = wins.find((w) => w.start <= now && now < w.end);
  if (!current) return null;
  const left = current.end - now;
  // another window running past this one's close, starting no later than a
  // minute after it (e.g. 23:59 -> 00:00), means the store isn't really closing
  const reopensSoon = wins.some((w) => w.end > current.end && w.start <= current.end + 1);
  return left < 30 && !reopensSoon ? `Closing in ${Math.ceil(left)} minutes` : null;
}

function cityOf(address) {
  if (address.city) return address.city;
  const parts = String(address.description || "").split(",");
  return parts.length > 1 ? parts[1].trim() : "";
}

function formatPhone(raw) {
  let d = String(raw || "").replace(/\D/g, "");
  if (d.length === 11 && d[0] === "1") d = d.slice(1);
  if (d.length !== 10) return raw || "";
  return `+1 (${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

// Raw franchise record -> the store shape Sidebar/MyMap render.
// live: true when the record came straight from the API (its "now" flags
// are current); false for the snapshot, where status is worked out from slots.
function toStore(f, fetchedAt, live) {
  const p = f.profile || {};
  const a = f.available_slots || {};
  const address = p.address || {};
  const override = storeOverrides[p.title] || {};

  const takeoutWins = windows(a.takeout_slots);
  const deliveryWins = windows(a.delivery_slots);
  const now = storeNow(a, fetchedAt);
  const inWindow = (wins) => wins.some((w) => w.start <= now && now < w.end);

  const takeoutNow = live ? !!a.is_takeout_order_now_available : inWindow(takeoutWins);
  const deliveryNow = live ? !!a.is_delivery_order_now_available : inWindow(deliveryWins);
  const isOpen = takeoutNow || deliveryNow;
  const temporarilyClosed =
    !!p.is_closed || (!a.is_takeout_order_available && !a.is_delivery_order_available);
  const comingSoon = temporarilyClosed && !!override.comingSoon;
  const allWins = windows([...(a.takeout_slots || []), ...(a.delivery_slots || [])]);

  return {
    name: override.name || String(p.name || p.title).replace(/^Wingz\s*-\s*/i, "Wingz "),
    address: address.description || "",
    status: isOpen ? "Open" : "Closed",
    reopen: !isOpen && !temporarilyClosed && !isNaN(now) ? nextOpeningText(allWins, now) : null,
    phone: formatPhone(p.phone_number),
    lat: address.lat,
    lng: address.lng,
    title: p.title,
    takeout: takeoutNow ? "Takeout Available" : "No Takeout",
    delivery: deliveryNow ? "Delivery Available" : "No Delivery",
    distance: null,
    closingSoon: isOpen && !isNaN(now) ? closingSoonText(allWins, now) : null,
    isComingSoon: comingSoon,
    temporarilyClosed,
    city: cityOf(address),
    orderOnlineLink: comingSoon ? "N/A" : `${ORDER_BASE}${p.title}/`,
  };
}

async function load() {
  try {
    const list = await fetchLive();
    const fetchedAt = Date.now();
    return list.map((f) => toStore(f, fetchedAt, true));
  } catch (err) {
    console.warn("Live store data unavailable, using snapshot:", err.message);
    const fetchedAt = Date.parse(snapshot.fetched_at);
    return (snapshot.franchises || []).map((f) => toStore(f, fetchedAt, false));
  }
}

// Fresh copies each call — callers mutate (distance, sorting).
export async function loadStores() {
  if (!cache || Date.now() - cache.at > CACHE_TTL_MS) {
    cache = { at: Date.now(), promise: load() };
  }
  const stores = await cache.promise;
  return stores.map((s) => ({ ...s }));
}
